'use client';

/**
 * RunbookModeSwitcher - Admin-only pill in the header for instant runbook switching.
 *
 * Shows the active mode abbreviation (e.g. "TPS", "CBSA", "EU") as a compact
 * clickable pill. On click, opens a popover listing all available runbooks.
 * Selecting one calls PUT /api/admin/runbook-mode, then router.refresh() to
 * re-render the RunbookConfig server component instantly.
 *
 * Visible only to platform admins. Other roles see nothing.
 */

import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import { useAppShell } from '@/components/shell/AppShellContext';

const SWITCHER_INVALIDATE_KEY = 'runbook-mode-changed-at';
const SWITCHER_INVALIDATE_WINDOW_MS = 90_000;

// ─── Types ────────────────────────────────────────────────────────────────────

interface RunbookOption {
  id: string;
  name: string;
  dbName: string;
  metadata: Record<string, string>;
  is_active: boolean;
}

interface ActiveRunbook {
  id: string;
  name: string;
  metadata: Record<string, string>;
}

// ─── Abbreviation helper ──────────────────────────────────────────────────────

function getAbbreviation(runbook: { id: string; metadata: Record<string, string> }): string {
  // Prefer the organization abbreviation from metadata
  if (runbook.metadata?.organization_abbreviation) {
    return runbook.metadata.organization_abbreviation;
  }
  // Fall back to uppercased first letters of the id segments
  return runbook.id
    .split('-')
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RunbookModeSwitcher() {
  const { user, isAuthLoading, accessControlState, authVersion, lastAuthRefresh } = useAppShell();
  const { userContext, loading, error } = accessControlState;
  // This endpoint is platform-only. Organization roles and injected display
  // metadata cannot grant access. A fresh DB context also retires pending work
  // when refetchAccessControl does not change the auth counters.
  const scope = useMemo(() => ({
    platformAdmin: !!user && !isAuthLoading && !loading && !error
      && userContext?.user_id === user.id && userContext.is_platform_admin === true,
  }), [user?.id, isAuthLoading, userContext, loading, error, authVersion, lastAuthRefresh]);
  const currentScope = useRef<typeof scope | null>(null);
  const router = useRouter();

  const [active, setActive] = useState<ActiveRunbook | null>(null);
  const [available, setAvailable] = useState<RunbookOption[]>([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [denied, setDenied] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Retire replies at commit time, before a previous request can settle. Clear
  // local state for a new user/context; a later authorized context can recover.
  useLayoutEffect(() => {
    currentScope.current = scope;
    setActive(null);
    setAvailable([]);
    setOpen(false);
    setSwitching(false);
    setDenied(false);
    return () => { if (currentScope.current === scope) currentScope.current = null; };
  }, [scope]);

  function denyCurrentScope() {
    if (currentScope.current !== scope) return;
    currentScope.current = null;
    setDenied(true);
    setActive(null);
    setAvailable([]);
    setOpen(false);
    setSwitching(false);
  }

  // Fetch available runbooks for the current platform-admin context.
  // Endpoint has Cache-Control: private, max-age=60, stale-while-revalidate=300.
  // template.tsx remounts AppShell on every SPA nav, so this effect re-fires
  // each navigation — we ride the browser cache for the GET (0ms repeat cost).
  // BUT: after a PUT, the cached GET would be stale and would overwrite our
  // fresh local state. We use a sessionStorage marker (survives remount) to
  // force a cache-bust for up to 90s after a switch.
  useEffect(() => {
    if (!scope.platformAdmin) return;
    let aborted = false;
    const isCurrent = () => !aborted && currentScope.current === scope;
    const injected = window.__RUNBOOK_CONFIG__?.__activeRunbook;
    if (injected) setActive(injected);
    // sessionStorage can throw SecurityError under strict ITP / corporate
    // policy. Treat any failure as "no marker" — fall through to cached fetch.
    let changedAt = 0;
    try {
      if (typeof window !== 'undefined') {
        changedAt = parseInt(sessionStorage.getItem(SWITCHER_INVALIDATE_KEY) || '0', 10);
      }
    } catch { /* sessionStorage blocked — proceed with default cache */ }
    const recentlyChanged = Date.now() - changedAt < SWITCHER_INVALIDATE_WINDOW_MS;
    const init: RequestInit = recentlyChanged ? { cache: 'reload' } : {};
    fetch('/api/admin/runbook-mode', init)
      .then(async (response) => {
        if (!isCurrent()) return;
        if (response.status === 401 || response.status === 403) {
          denyCurrentScope();
          return;
        }
        if (!response.ok) return;
        const data = await response.json();
        if (!isCurrent()) return;
        if (data.available) setAvailable(data.available);
        if (data.active) setActive(data.active);
        // Once we've seen fresh data, drop the invalidation marker so the
        // next mount (within max-age) can use the cache normally.
        if (recentlyChanged && typeof window !== 'undefined') {
          try { sessionStorage.removeItem(SWITCHER_INVALIDATE_KEY); } catch {}
        }
      })
      .catch(() => {/* silent — non-critical */});
    return () => { aborted = true; };
  }, [scope]);

  // Close on click outside
  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  if (!scope.platformAdmin || denied) return null;
  if (!active && available.length === 0) return null;

  const abbreviation = active ? getAbbreviation(active) : '…';

  async function switchTo(dbName: string) {
    if (switching || currentScope.current !== scope) return;
    setSwitching(true);
    setOpen(false);
    let succeeded = false;
    try {
      const res = await fetch('/api/admin/runbook-mode', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: dbName }),
      });
      if (currentScope.current !== scope) return;
      if (res.status === 401 || res.status === 403) {
        denyCurrentScope();
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      if (currentScope.current !== scope) return;
      if (data.success && data.active) {
        succeeded = true;
        setActive(data.active);
        // Update is_active flags in the available list so the dropdown highlights correctly
        setAvailable((prev) =>
          prev.map((rb) => ({ ...rb, is_active: rb.dbName === dbName }))
        );
        // Marker survives template-driven remount; the mount effect uses it
        // to cache-bust the next GET so we don't read a pre-switch response
        // from the browser cache and overwrite local state with stale data.
        if (typeof window !== 'undefined') {
          try {
            sessionStorage.setItem(SWITCHER_INVALIDATE_KEY, String(Date.now()));
          } catch {/* sessionStorage blocked — fall back to 60s cache window */}
        }
      }
    } catch {
      // Non-critical transport failure; keep the current control available.
    } finally {
      // Re-enable the pill before triggering refresh — router.refresh() can suspend
      // the component tree while waiting for the server response, which would leave
      // the pill stuck in disabled/spinner state if setSwitching ran after.
      if (currentScope.current === scope) setSwitching(false);
    }
    // Trigger full server component re-render AFTER local state is settled.
    // This runs outside try/finally so the pill is already interactive.
    if (succeeded && currentScope.current === scope) router.refresh();
  }

  return (
    <div ref={popoverRef} className="relative">
      {/* Pill button */}
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={switching}
        title={active?.name ?? 'Switch runbook mode'}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-all duration-150
          bg-indigo-500/10 border-indigo-500/30 text-indigo-300
          hover:bg-indigo-500/20 hover:border-indigo-400/50
          disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {switching ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : null}
        <span>{abbreviation}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 min-w-[220px] rounded-lg border border-slate-700 bg-slate-900 shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-800">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Runbook Mode</p>
          </div>
          <ul className="py-1">
            {available.map((rb) => {
              const abbr = getAbbreviation(rb);
              const isActive = rb.is_active;
              return (
                <li key={rb.dbName}>
                  <button
                    onClick={() => !isActive && switchTo(rb.dbName)}
                    disabled={isActive || switching}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors
                      ${isActive
                        ? 'bg-indigo-500/10 cursor-default'
                        : 'hover:bg-slate-800 cursor-pointer'}
                    `}
                  >
                    {/* Abbreviation badge */}
                    <span className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold
                      ${isActive
                        ? 'bg-indigo-500/20 text-indigo-300'
                        : 'bg-slate-700 text-slate-400'}`}
                    >
                      {abbr}
                    </span>

                    {/* Labels */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : 'text-slate-300'}`}>
                        {rb.name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {rb.metadata?.organization ?? rb.dbName}
                      </p>
                    </div>

                    {/* Active checkmark */}
                    {isActive && <Check className="w-4 h-4 text-indigo-400 flex-shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
